<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeCheckbox from "@dsk/components/Checkbox/LeCheckbox.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeTextarea from "@dsk/components/Form/LeTextarea.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeModal from "@dsk/components/LeModal.vue";
import type { ModalProps } from "@dsk/types";
import { useField, useForm } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { ValidationHelper } from "@/helpers";
import { UpsellAccessRequestService } from "@/services";
import type { Dictionary } from "@/types";
import { type UpsellAccessRequest, upsellAccessRequestFormValidator } from "@/validation";

const props = defineProps<
  ModalProps & {
    dictionary: Dictionary;
  }
>();

const emit = defineEmits<{
  close: [didSubmit: boolean];
}>();

const { t } = useI18n();

const { handleSubmit, errors, resetForm } = useForm({
  initialValues: {
    dictionaryId: props.dictionary.id,
    dictionaryTitle: props.dictionary.title,
    name: "",
    email: "",
    phone: "",
    comment: "",
    acceptMarketing: false
  },
  validationSchema: upsellAccessRequestFormValidator
});

const { value: name, meta: nameMeta } = useField<string>("name");
const { value: email, meta: emailMeta } = useField<string>("email");
const { value: phone, meta: phoneMeta } = useField<string>("phone");
const { value: comment, meta: commentMeta } = useField<string>("comment");
const { value: acceptMarketing, meta: acceptMarketingMeta } = useField<boolean>("acceptMarketing");

const hasApiError = ref<boolean>(false);
const onSubmit = handleSubmit(async (form) => {
  hasApiError.value = false;

  try {
    await UpsellAccessRequestService.submit(form as UpsellAccessRequest);

    onClose(true);
  } catch (error) {
    hasApiError.value = true;
  }
});

const onClose = (didSubmit: boolean) => {
  resetForm();
  emit("close", didSubmit);
};
</script>

<template>
  <LeModal class="le-upsell-access-request-modal" :is-open="props.isOpen" @close="onClose(false)">
    <template #title>
      <LeHeading tag="h1" size="m">{{ t("le-upsell.modal.title") }}</LeHeading>
    </template>

    <template #body>
      <p class="le-upsell-access-request-modal__text">
        <i18n-t tag="span" keypath="le-upsell.modal.text">
          <template #dictionary>
            <span class="le-upsell-access-request-modal__text le-upsell-access-request-modal__text--emphasize">
              {{ props.dictionary.title }}
            </span>
          </template>
        </i18n-t>
      </p>

      <form
        id="le-upsell-access-request-form"
        class="le-upsell-access-request-modal__form"
        @submit.prevent="onSubmit()"
      >
        <div class="row">
          <div v-if="hasApiError" class="col-xs-12" role="alert">
            <p class="le-upsell-access-request-modal__alert">
              {{ t("le-upsell.modal.error.text") }}
            </p>
          </div>
          <div class="col-xs-12">
            <div class="le-upsell-access-request-modal__form-group">
              <LeInput
                v-model="name"
                :label="t('le-upsell.modal.form.name.label')"
                :placeholder="t('le-upsell.modal.form.name.placeholder')"
                :has-error="ValidationHelper.displayError(nameMeta)"
              />
              <small v-if="errors.name" class="le-upsell-access-request-modal__form-error-text">{{
                errors.name
              }}</small>
            </div>
            <div class="le-upsell-access-request-modal__form-group">
              <LeInput
                v-model="email"
                :label="t('le-upsell.modal.form.email.label')"
                :placeholder="t('le-upsell.modal.form.email.placeholder')"
                :has-error="ValidationHelper.displayError(emailMeta)"
              />
              <small v-if="errors.email" class="le-upsell-access-request-modal__form-error-text">{{
                errors.email
              }}</small>
            </div>
            <div class="le-upsell-access-request-modal__form-group">
              <LeInput
                v-model="phone"
                :label="t('le-upsell.modal.form.phone.label')"
                :placeholder="t('le-upsell.modal.form.phone.placeholder')"
                :has-error="ValidationHelper.displayError(phoneMeta)"
              />
              <small v-if="errors.phone" class="le-upsell-access-request-modal__form-error-text">{{
                errors.phone
              }}</small>
            </div>
            <div class="le-upsell-access-request-modal__form-group">
              <LeTextarea
                v-model="comment"
                class="le-upsell-access-request-modal__form-comment"
                :label="t('le-upsell.modal.form.comment.label')"
                :placeholder="t('le-upsell.modal.form.comment.placeholder')"
                rows="1"
                :has-error="ValidationHelper.displayError(commentMeta)"
              />
              <small v-if="errors.comment" class="le-upsell-access-request-modal__form-error-text">{{
                errors.comment
              }}</small>
            </div>
            <label class="le-upsell-access-request-modal__form-accept-marketing">
              <LeCheckbox
                v-model="acceptMarketing"
                class="le-upsell-access-request-modal__form-accept-marketing-checkbox"
              />
              <span
                class="le-upsell-access-request-modal__text le-upsell-access-request-modal__text--newlines"
                :class="{
                  'le-upsell-access-request-modal__text--error': ValidationHelper.displayError(acceptMarketingMeta)
                }"
              >
                {{ t("le-upsell.modal.accept-marketing.text") }}
              </span>
            </label>
            <p class="le-upsell-access-request-modal__required-info-text">
              {{ t("le-upsell.modal.mandatory.text") }}
            </p>
          </div>
        </div>
      </form>
    </template>

    <template #buttons>
      <LeButton variant="outline" @click="onClose(false)">
        {{ t("le-upsell.modal.button.cancel") }}
      </LeButton>
      <LeButton type="submit" form="le-upsell-access-request-form">
        {{ t("le-upsell.modal.button.submit") }}
      </LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-upsell-access-request-modal {
  &__alert {
    width: 100%;
    margin-bottom: 12px;
    padding: 12px 16px;
    border: none;
    background-color: @colors[@error][200];
    .body-text("s");
    color: @colors[@error][900];
  }

  &__text {
    margin-bottom: 0;
    .body-text("m");

    &--emphasize {
      .body-text("m", "semi-bold");
    }

    &--newlines {
      white-space: pre-line;
    }

    &--error {
      color: @colors[@error][600];
    }
  }

  &__form {
    margin-top: 10px;
    margin-bottom: 15px;

    #media-breakpoint-up(md, {
      margin-top: 20px;
      margin-bottom: 30px;
    });

    :deep(&-comment) {
      max-height: 100px;
    }

    &-accept-marketing {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin: 8px 0;

      #media-breakpoint-up(md, {
        margin: 20px 0;
      });

      &-checkbox {
        margin-top: 2px;
      }
    }
  }

  &__form-group {
    margin-bottom: 12px;
  }

  &__form-error-text {
    .body-text("xs");
    color: @colors[@error][600];
  }

  &__required-info-text {
    margin-top: 16px;
    margin-bottom: 0;
    .body-text("s");
    color: @colors[@neutrals][200];
  }
}
</style>
